import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Typography, Button, ButtonGroup } from "@mui/material";
import { useExperiences } from "../context/ExperiencesContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import GuideCard from "../components/GuideCard";
import ExperiencesApi from "../services/ExperiencesAPI";

const GuidesListPage = () => {
  const { guides, deleteGuide, fetchGuides } = useExperiences();
  const auth = useAuth();
  const navigate = useNavigate();

  const [sortKey, setSortKey] = useState("date"); // "date" | "name"

  const extractGuideId = (guide) => guide.sk.split("GUIDE#")[1];

  const safeDate = (g) => new Date(g?.dateCreated ?? g?.dateModified ?? g?.lastModified ?? 0).getTime();

  const sorters = {
    date: (a, b) => safeDate(b) - safeDate(a), // most recent first
    name: (a, b) => (a?.name ?? "").localeCompare(b?.name ?? "", undefined, { sensitivity: "base" }),
  };

  const { currentUserGuides, otherGuides } = useMemo(() => {
    const mine = (guides ?? []).filter((g) => g.creator === auth.userId);
    const others = (guides ?? []).filter((g) => g.creator !== auth.userId);
    const sorter = sorters[sortKey] ?? sorters.date;
    return {
      currentUserGuides: [...mine].sort(sorter),
      otherGuides: [...others].sort(sorter),
    };
  }, [guides, auth.userId, sortKey]);


  useEffect(() => {
    fetchGuides();
  }, [])

  const handleEdit = (guideId) => navigate(`/guides/${guideId}`);
  const handleClone = (guide) => navigate(`/guides/new?cloneFrom=${extractGuideId(guide)}`);
  const handleCreateNew = () => navigate("/guides/new");

  const handleDelete = async (guideId) => {
    try {
      await deleteGuide(guideId); // optimistic remove; refetch on failure
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
        <Button variant="contained" onClick={handleCreateNew}>
          Create New Guide
        </Button>

        <ButtonGroup size="small" aria-label="sort guides">
          <Button variant={sortKey === "date" ? "contained" : "outlined"} onClick={() => setSortKey("date")}>
            Sort: Date Added
          </Button>
          <Button variant={sortKey === "name" ? "contained" : "outlined"} onClick={() => setSortKey("name")}>
            Sort: Name
          </Button>
        </ButtonGroup>
      </Box>

      <Typography variant="h5" sx={{ marginBottom: 1 }}>
        My Guides
      </Typography>
      <Grid container spacing={2}>
        {currentUserGuides.length > 0 ? (
          currentUserGuides.map((guide) => (
            <Grid item xs={12} sm={6} md={4} key={extractGuideId(guide)}>
              <GuideCard guide={guide} isOwner onEdit={handleEdit} onClone={handleClone} onDelete={handleDelete} />
            </Grid>
          ))
        ) : (
          <Typography>No guides available.</Typography>
        )}
      </Grid>

      <Typography variant="h5" sx={{ marginTop: 4, marginBottom: 1 }}>
        All Other Guides
      </Typography>
      <Grid container spacing={2}>
        {otherGuides.length > 0 ? (
          otherGuides.map((guide) => (
            <Grid item xs={12} sm={6} md={4} key={extractGuideId(guide)}>
              <GuideCard
                guide={guide}
                isOwner={false}
                onEdit={handleEdit}
                onClone={handleClone}
                onDelete={handleDelete}
              />
            </Grid>
          ))
        ) : (
          <Typography>No other guides available.</Typography>
        )}
      </Grid>
    </Box>
  );
};

export default GuidesListPage;
